import React, { useState } from 'react';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import KYCManagementPanel from './components/KYCManagementPanel';
import PaymentOversightPanel from './components/PaymentOversightPanel';
import UserAdministrationPanel from './components/UserAdministrationPanel';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import ComplianceReportsPanel from './components/ComplianceReportsPanel';
import SystemHealthPanel from './components/SystemHealthPanel';

const AdminControlPanel = () => {
  const [activeTab, setActiveTab] = useState('kyc');

  const tabs = [
    { id: 'kyc', label: 'KYC Management', icon: 'UserCheck' },
    { id: 'payments', label: 'Payment Oversight', icon: 'DollarSign' },
    { id: 'users', label: 'User Administration', icon: 'Users' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3' },
    { id: 'compliance', label: 'Compliance Reports', icon: 'FileText' },
    { id: 'system', label: 'System Health', icon: 'Activity' }
  ];

  const renderTabContent = () => {
    switch(activeTab) {
      case 'kyc':
        return <KYCManagementPanel />;
      case 'payments':
        return <PaymentOversightPanel />;
      case 'users':
        return <UserAdministrationPanel />;
      case 'analytics':
        return <AnalyticsDashboard />;
      case 'compliance':
        return <ComplianceReportsPanel />;
      case 'system':
        return <SystemHealthPanel />;
      default:
        return <KYCManagementPanel />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <BreadcrumbNavigation />
      <main className="main-content">
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4 md:py-6 lg:py-8">
          <div className="mb-4 sm:mb-5 md:mb-6 lg:mb-8">
            <div className="flex items-center gap-2 sm:gap-3 md:gap-4 mb-2">
              <div className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 rounded-lg bg-gradient-to-br from-[#1e4a72] to-[#ff8c00] flex items-center justify-center flex-shrink-0">
                <Icon name="Settings" size={18} className="sm:hidden" color="#FFFFFF" />
                <Icon name="Settings" size={20} className="hidden sm:block md:hidden" color="#FFFFFF" />
                <Icon name="Settings" size={24} className="hidden md:block lg:hidden" color="#FFFFFF" />
                <Icon name="Settings" size={28} className="hidden lg:block" color="#FFFFFF" />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-bold text-foreground truncate">
                  Admin Control Panel
                </h1>
                <p className="text-xs sm:text-sm md:text-base text-muted-foreground truncate">
                  Comprehensive platform oversight and management
                </p>
              </div>
            </div>
          </div>

          <div className="hidden md:block bg-card rounded-lg border border-border mb-4 md:mb-6 shadow-sm">
            <div className="flex items-center gap-1.5 lg:gap-2 p-1.5 lg:p-2 overflow-x-auto scrollbar-hide">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-1.5 lg:gap-2 px-3 md:px-4 lg:px-5 py-2.5 md:py-3 rounded-md text-xs md:text-sm lg:text-base font-medium transition-all whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] text-white shadow-lg scale-[1.02]'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground hover:scale-[1.01]'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} className="md:hidden" />
                  <Icon name={tab?.icon} size={18} className="hidden md:block" />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="md:hidden mb-4 sm:mb-5">
            <div className="relative">
              <select
                value={activeTab}
                onChange={(e) => setActiveTab(e?.target?.value)}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-card border border-border rounded-lg text-sm sm:text-base text-foreground font-medium appearance-none cursor-pointer focus:ring-2 focus:ring-[#ff8c00] focus:border-transparent shadow-sm"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.5rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em',
                  paddingRight: '2.5rem'
                }}
              >
                {tabs?.map((tab) => (
                  <option key={tab?.id} value={tab?.id}>
                    {tab?.label}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Icon 
                  name={tabs?.find(t => t?.id === activeTab)?.icon || 'Settings'} 
                  size={16} 
                  className="sm:hidden text-[#ff8c00]"
                />
                <Icon 
                  name={tabs?.find(t => t?.id === activeTab)?.icon || 'Settings'} 
                  size={18} 
                  className="hidden sm:block text-[#ff8c00]"
                />
              </div>
            </div>
          </div>

          <div className="transition-all duration-300 ease-in-out">
            {renderTabContent()}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminControlPanel;